import { Button, LoadingSpinner } from "@/components/ui";
import type { Matter, MatterPhase, PhaseInformationWidget } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { ErrorBoundary } from "@sentry/react";
import { useQueryErrorResetBoundary } from "@tanstack/react-query";
import dayjs from "dayjs";
import { Clock3 } from "lucide-react";
import { Suspense, useEffect, useState } from "react";
import { TreatmentTimeline } from "../components/TreatmentTimeline";
import {
  CycleTimeSummary,
  FinalLienResolutions,
  SettlementSummary,
} from "../phases/ClosedSection";
import { CoverageTable, SentDemands } from "../phases/DemandsCard";
import { LiabilityCoverage } from "../phases/InvestigationCard";
import { getPendingDemandCoverageGroups } from "../phases/pending-demand-coverage-utils";
import { ProvidersSection, TreatmentSummary } from "../phases/TreatingSection";

const DAY_MS = 24 * 60 * 60 * 1000;

export function MatterPhaseCountdown({
  title,
  durationDays,
  startedAt,
}: {
  title: string;
  durationDays: number;
  startedAt?: string | null;
}) {
  const [now, setNow] = useState(() => Date.now());
  // Database datetimes are UTC even when the API omits their offset. Keep
  // explicit offsets intact instead of interpreting a naive value as local time.
  const start = startedAt
    ? Date.parse(
        /(?:Z|[+-]\d{2}:?\d{2})$/i.test(startedAt)
          ? startedAt
          : `${startedAt}Z`,
      )
    : Number.NaN;
  const hasStarted = Number.isFinite(start);
  const deadline = start + durationDays * DAY_MS;

  useEffect(() => {
    // Only the local clock advances. There is no network polling.
    const interval = window.setInterval(() => {
      setNow(Date.now());
    }, 60_000);
    return () => {
      window.clearInterval(interval);
    };
  }, []);

  const daysRemaining = hasStarted
    ? Math.min(durationDays, Math.max(0, Math.ceil((deadline - now) / DAY_MS)))
    : durationDays;
  const daysOverdue = hasStarted
    ? Math.max(0, Math.ceil((now - deadline) / DAY_MS))
    : 0;
  const progress = hasStarted
    ? Math.min(
        100,
        Math.max(0, ((now - start) / (durationDays * DAY_MS)) * 100),
      )
    : 0;

  return (
    <div className="min-w-0">
      <h3 className="border-b pb-4 text-sm font-semibold break-words">
        {title}
      </h3>
      <div className="flex min-h-44 flex-col justify-between gap-6 pt-5">
        <div>
          <p
            className={cn(
              "flex items-center gap-2 text-2xl font-bold",
              daysOverdue > 0 && "text-red-700",
            )}
          >
            <Clock3 className="h-5 w-5 shrink-0" aria-hidden="true" />
            {!hasStarted
              ? "Waiting for phase to start"
              : daysOverdue > 0
                ? `${daysOverdue} ${daysOverdue === 1 ? "day" : "days"} overdue`
                : `${daysRemaining} ${daysRemaining === 1 ? "day" : "days"} remaining`}
          </p>
          <p className="text-muted-foreground mt-2 text-sm">
            {hasStarted
              ? `Started ${dayjs(start).format("MMM D, YYYY")} · due ${dayjs(deadline).format("MMM D, YYYY")}`
              : `${durationDays}-day countdown begins when this phase first makes progress.`}
          </p>
        </div>
        <div>
          <div
            role="progressbar"
            aria-label={`${title} elapsed time`}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progress)}
            className="bg-muted h-2 overflow-hidden rounded-full"
          >
            <div
              className={cn(
                "h-full rounded-full bg-amber-400",
                daysOverdue > 0 && "bg-red-500",
              )}
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="text-muted-foreground mt-2 flex justify-between text-xs">
            <span>Day 0</span>
            <span>Day {durationDays}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function InformationWidgetContent({
  widget,
  phase,
  matter,
}: {
  widget: PhaseInformationWidget;
  phase: MatterPhase;
  matter: Matter;
}) {
  const props = { matter, title: widget.title, embedded: true };
  switch (widget.type) {
    case "Countdown":
      return (
        <MatterPhaseCountdown
          title={widget.title}
          durationDays={widget.durationDays}
          startedAt={phase.startedAt}
        />
      );
    case "Coverage":
      return (
        <CoverageTable
          {...props}
          groups={getPendingDemandCoverageGroups(matter, {
            includeExplicitTypes: true,
          })}
          compactLimits={false}
        />
      );
    case "TreatmentTimeline":
      return <TreatmentTimeline {...props} />;
    case "LiabilityCoverage":
      return <LiabilityCoverage {...props} />;
    case "TreatmentSummary":
      return <TreatmentSummary {...props} />;
    case "SentDemands":
      return <SentDemands {...props} />;
    case "Providers":
      return <ProvidersSection {...props} />;
    case "SettlementSummary":
      return <SettlementSummary {...props} />;
    case "CycleTimeSummary":
      return <CycleTimeSummary {...props} />;
    case "FinalLienResolutions":
      return <FinalLienResolutions {...props} />;
  }
}

export function MatterPhaseInformationWidget(props: {
  widget: PhaseInformationWidget;
  phase: MatterPhase;
  matter: Matter;
}) {
  const queryErrorBoundary = useQueryErrorResetBoundary();
  return (
    <section
      aria-label={props.widget.title}
      className="border-border/70 bg-card h-full min-w-0 overflow-hidden rounded-2xl border p-5"
    >
      <ErrorBoundary
        key={`${props.matter.id}:${props.widget.id}`}
        onReset={() => {
          queryErrorBoundary.reset();
        }}
        fallback={(errorBoundary) => (
          <div>
            <h3 className="border-b pb-4 text-sm font-semibold break-words">
              {props.widget.title}
            </h3>
            <p role="alert" className="text-muted-foreground py-5 text-sm">
              This widget could not be loaded. Other phase actions are still
              available.
            </p>
            <Button
              variant="outline"
              onClick={() => {
                errorBoundary.resetError();
              }}
            >
              Retry widget
            </Button>
          </div>
        )}
      >
        <Suspense
          fallback={
            <div
              className="flex min-h-36 items-center justify-center"
              aria-label={`Loading ${props.widget.title}`}
            >
              <LoadingSpinner className="h-5 w-5" />
            </div>
          }
        >
          <InformationWidgetContent {...props} />
        </Suspense>
      </ErrorBoundary>
    </section>
  );
}
